/**
 * # Search Banner
 */

/**
 * ## Imports
 */
import './SearchBanner.scss';

import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../images/icons/close-icon.svg';
import PickBannerVariation from './PickBannerVariation';
import SearchBannerTextVariations from './TextVariations';

interface BannerContent {
    id: number;
    headline: string;
    copyText: string;
    wasClosedByUser: boolean;
    wasShownForOneHour: boolean;
}

const SearchBanner = () => {
    const { t } = useTranslation('common');
    const storedContent = localStorage.getItem('homepageExcludedMarketingBanner');
    const availableContent = SearchBannerTextVariations.filter(
        (content) => !storedContent || !storedContent.includes(content.id.toString()),
    );
    const storedMultiplier = localStorage.getItem('homepageTimeMultiplier');
    let timeMultiplier = storedMultiplier ? Number(storedMultiplier) : 60;

    /**
     * ### State
     */
    const [pickedContent, setPickedContent] = useState<BannerContent | null>(null);
    const [visitsCount, setVisitsCount] = useState(0);
    const [showBanner, setShowBanner] = useState(false);
    const [isBannerVisible, setIsBannerVisible] = useState(false);

    const incrementVisitsCount = () => {
        setVisitsCount((prevCount) => prevCount + 1);
    };

    const makeLastItemUnavailable = () => {
        const lastItemDisplayed = parseInt(localStorage.getItem('homepageLastPickedItem'));

        const updatedContent = storedContent ? JSON.parse(storedContent) : [];
        if (lastItemDisplayed && !updatedContent.includes(lastItemDisplayed)) {
            updatedContent.push(lastItemDisplayed);
            localStorage.setItem('homepageExcludedMarketingBanner', JSON.stringify(updatedContent));
        }
    };

    useEffect(() => {
        /* - Setting up a time multiplier that can be manipulated in the
         * localStorage. (For testing purposes) */
        localStorage.setItem('homepageTimeMultiplier', timeMultiplier.toString());
    }, [timeMultiplier]);

    /**
     * ## Set Banner Content / Count Visits
     */
    useEffect(() => {
        const newPickedContent = PickBannerVariation(availableContent);
        setPickedContent(newPickedContent);

        const count = localStorage.getItem('homepageVisitsCount');
        if (count) {
            setVisitsCount(Number(count));
        }
        incrementVisitsCount();

        const storedTimestamp = sessionStorage.getItem('homepageTimestamp');
        const currentTime = Date.now();

        /* - Every hour increase `homepageTimestamp` by `1`: */
        if (storedTimestamp) {
            const initialTime = Number(storedTimestamp);
            const hoursPassed = Math.ceil((currentTime - initialTime) / (60 * timeMultiplier * 1000));

            if (hoursPassed > 1) {
                makeLastItemUnavailable();
            }
        } else {
            sessionStorage.setItem('homepageTimestamp', currentTime.toString());
        }
        //eslint-disable-next-line
    }, [timeMultiplier]);

    /**
     * ## Count Page Visits
     * - Display a banner on the first and every third visit.
     */
    useEffect(() => {
        if (visitsCount === 1 || (visitsCount - 1) % 3 === 0) {
            setShowBanner(true);
            localStorage.setItem('homepageLastPickedItem', pickedContent?.id);
        } else {
            setShowBanner(false);
        }

        localStorage.setItem('homepageVisitsCount', visitsCount.toString());
        //eslint-disable-next-line
    }, [visitsCount]);

    const handleBannerClose = () => {
        makeLastItemUnavailable();

        setShowBanner(false);
        setIsBannerVisible(false);
    };

    /**
     * ## Apply the CSS class which contains the animation a bit
     * later to ensure that also iPhone devices show the transition.
     */
    useEffect(() => {
        let timeoutId;

        if (showBanner) {
            timeoutId = setTimeout(() => {
                setIsBannerVisible(true);
            }, 100);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [showBanner]);

    return (
        <>
            {pickedContent ? (
                <div className="search-banner-wrapper">
                    <section
                        className={cx('search-banner', {
                            'search-banner--visible': isBannerVisible,
                        })}
                    >
                        <div className="search-banner__text-body">
                            <h2 className="search-banner__headline">{t(`${pickedContent?.headline}`)}</h2>
                            <p className="search-banner__copytext">{t(`${pickedContent?.copyText}`)}</p>
                        </div>
                        <button type="button" className="search-banner__close-button" onClick={handleBannerClose}>
                            <span className="sr-only">{t('tips-card-close-button', 'Close')}</span>
                            <CloseIcon />
                        </button>
                    </section>
                </div>
            ) : null}
        </>
    );
};

export default SearchBanner;
